import { useState } from 'react';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => formBody.append(key, value));
      const response = await fetch('https://readdy.ai/api/form/d6ppsp6a739gopmlgv70', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', company: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = "w-full px-3 md:px-4 py-2.5 md:py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all";
  const labelClass = "block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2";

  return (
    <section id="contact" className="py-14 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-16">
          <h2 className="text-2xl md:text-5xl font-bold text-slate-800 mb-3 md:mb-6">
            まずは無料相談から
          </h2>
          <p className="text-sm md:text-lg text-gray-600 max-w-2xl mx-auto">
            飲食店の開業・経営改善のお悩み、お気軽にご相談ください
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16">
          <div>
            <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6" data-readdy-form>
              <div>
                <label htmlFor="name" className={labelClass}>お名前 <span className="text-red-500">*</span></label>
                <input type="text" id="name" name="name" required value={formData.name} onChange={handleChange} className={inputClass} placeholder="山田 太郎" />
              </div>
              <div>
                <label htmlFor="email" className={labelClass}>メールアドレス <span className="text-red-500">*</span></label>
                <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} className={inputClass} placeholder="example@email.com" />
              </div>
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                <div>
                  <label htmlFor="phone" className={labelClass}>電話番号</label>
                  <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className={inputClass} placeholder="090-1234-5678" />
                </div>
                <div>
                  <label htmlFor="company" className={labelClass}>店舗名・会社名</label>
                  <input type="text" id="company" name="company" value={formData.company} onChange={handleChange} className={inputClass} placeholder="株式会社〇〇" />
                </div>
              </div>
              <div>
                <label htmlFor="service" className={labelClass}>ご相談内容 <span className="text-red-500">*</span></label>
                <select id="service" name="service" required value={formData.service} onChange={handleChange} className={`${inputClass} cursor-pointer`}>
                  <option value="">選択してください</option>
                  <option value="店舗開業支援">店舗開業支援</option>
                  <option value="経営コンサルティング">経営コンサルティング</option>
                  <option value="メニュー開発">メニュー開発</option>
                  <option value="スタッフ研修">スタッフ研修</option>
                  <option value="その他">その他</option>
                </select>
              </div>
              <div>
                <label htmlFor="message" className={labelClass}>メッセージ <span className="text-red-500">*</span></label>
                <textarea id="message" name="message" required rows={4} maxLength={500} value={formData.message} onChange={handleChange} className={`${inputClass} resize-none`} placeholder="ご相談内容をお聞かせください（500文字以内）"></textarea>
                <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500文字</p>
              </div>
              <button type="submit" disabled={isSubmitting} className="w-full bg-teal-700 text-white px-8 py-3 md:py-4 rounded-lg text-sm md:text-base font-medium hover:bg-teal-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer">
                {isSubmitting ? '送信中...' : '無料相談を申し込む'}
              </button>
              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                  お問い合わせありがとうございます。担当者より折り返しご連絡いたします。
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                  送信に失敗しました。もう一度お試しください。
                </div>
              )}
            </form>
          </div>

          <div className="space-y-6 md:space-y-8">
            <div>
              <h3 className="text-lg md:text-2xl font-bold text-slate-800 mb-4 md:mb-6">お問い合わせ先</h3>
              <div className="space-y-3 md:space-y-4">
                {[
                  { icon: 'ri-map-pin-line', title: '所在地', content: '〒231-0806\n神奈川県横浜市中区本牧町2丁目304\n加藤ビル303D室' },
                  { icon: 'ri-phone-line', title: '電話番号', content: 'TEL: 050-1720-6460\nMobile: 080-1133-6460' },
                  { icon: 'ri-mail-line', title: 'メールアドレス', content: 'hawa0816@yahoo.co.jp' },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-3 md:gap-4">
                    <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-teal-100 rounded-lg flex-shrink-0">
                      <i className={`${item.icon} text-lg md:text-2xl text-teal-700`}></i>
                    </div>
                    <div>
                      <div className="text-sm md:text-base font-semibold text-slate-800 mb-0.5">{item.title}</div>
                      <div className="text-xs md:text-base text-gray-600 whitespace-pre-line">{item.content}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl md:rounded-2xl p-5 md:p-8">
              <h4 className="text-base md:text-xl font-bold text-slate-800 mb-3 md:mb-4">営業時間</h4>
              <div className="space-y-1 md:space-y-2 text-sm md:text-base text-gray-700">
                <p>平日: 10:00 - 18:00</p>
                <p>土日祝: 定休日</p>
              </div>
              <p className="text-xs text-gray-600 mt-3 md:mt-4">
                ※お急ぎの場合は、お電話にてお問い合わせください
              </p>
            </div>

            <div className="rounded-xl md:rounded-2xl overflow-hidden">
              <div className="h-48 md:h-64 w-full">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3251.0!2d139.65!3d35.43!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzXCsDI1JzQ4LjAiTiAxMznCsDM5JzAwLjAiRQ!5e0!3m2!1sja!2sjp!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="栄伸株式会社 所在地"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
